import { useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSubscription } from '@apollo/client';
import sha256 from 'crypto-js/sha256';
import { useSelector } from 'react-redux';
import { omit, values } from 'ramda';

import { useLazyQuery } from 'api';
import { getUserEmail } from 'containers/authorization/selectors';
import staticData from 'containers/books/data';
import { FLIP_BOOKS } from 'containers/books/const';
import { useAsyncState } from 'hooks';

import { SEND_MESSAGE, AI_MESSAGE } from './api';

const BOOKS = values(staticData).reduce((acc, { book }) => (book.format === FLIP_BOOKS ? { ...acc, [book.id]: book } : acc), {});

const formatBoldText = (text) => text?.replace(/\*\*([^*]+)\*\*/g, '<b>$1</b>') ?? text;

export const useChat = () => {
  const { t } = useTranslation('app');
  const previousResponseId = useRef();
  const theFirstId = useMemo(() => Date.now(), []);
  const [messages, setMessages] = useAsyncState({});
  const onData = useCallback(
    ({ data: answer }) => {
      const { id, posts: videos, responseId, response, status, startedAt, flipbooks } = answer.data.aiSearchCompleted;

      previousResponseId.current = responseId;

      setMessages(($) => ({
        ...omit(['wait'], $),
        [id]: {
          id,
          role: 'assistant',
          content: formatBoldText(response),
          posts: videos,
          books: flipbooks.map((bookId) => BOOKS[bookId]).filter(Boolean),
          status,
          timestamp: startedAt,
        },
      }));
    },
    [setMessages]
  );
  const [action, { loading, error }] = useLazyQuery(SEND_MESSAGE, { fetchPolicy: 'no-cache' });
  const email = useSelector(getUserEmail);
  const variables = useMemo(() => ({ hash: sha256(email).toString() }), [email]);

  useSubscription(AI_MESSAGE, { variables, shouldResubscribe: true, onData });

  const sendMessage = useCallback(
    async (message) => {
      const msg = message.trim();

      if (msg) {
        action({ variables: { query: msg, previousResponseId: previousResponseId.current } });
        setMessages(($) => {
          const now = Date.now();
          const timestamp = new Date();

          return {
            ...$,
            [now]: {
              id: now,
              role: 'user',
              content: msg,
              posts: null,
              books: null,
              status: 1,
              timestamp,
            },
            wait: {
              id: 'wait',
              role: 'assistant',
              content: '',
              posts: null,
              books: null,
              status: 0,
              timestamp,
            },
          };
        });
      }
    },
    [action, setMessages]
  );

  return {
    messages: useMemo(
      () =>
        values({
          [theFirstId]: {
            id: theFirstId,
            role: 'assistant',
            content: t('Im your AI assistant'),
            posts: null,
            books: null,
            status: 1,
            timestamp: new Date(theFirstId),
          },
          ...messages,
        }),
      [theFirstId, t, messages]
    ),
    sendMessage,
    loading,
    error,
  };
};
